import { PropsWithChildren, ReactNode } from 'react';
import { Tr } from '@utils';
import { Box, Button, List, ListProps, Stack, StackProps, Typography } from '@mui/material';

export interface IEntityListProps<EntityType> extends StackProps {
	paddingX?: number | string;
	empty?: ReactNode;
	hideSelectAllButton?: boolean;
	showResultCount?: boolean;
	entities: EntityType[];
	selected?: EntityType[];
	listProps?: ListProps;
	onSelectChange?: (selected: EntityType[]) => void;
}

export const EntityList = <EntityType extends Record<string, any>>({
	paddingX,
	empty,
	hideSelectAllButton,
	showResultCount,
	entities,
	selected,
	listProps: { sx: listSx, ...listProps } = {},
	onSelectChange,
	sx,
	children,
	...props
}: PropsWithChildren<IEntityListProps<EntityType>>) => {
	return (
		<Stack
			direction="column"
			spacing={1}
			sx={{ flexGrow: 1, maxHeight: 1.0, overflow: 'hidden', paddingLeft: paddingX, ...sx }}
			{...props}
		>
			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<Typography>
					{showResultCount ? (
						<Tr.Portal path={entities.length > 1 ? 'results' : 'result'} options={{ length: entities.length }} />
					) : null}
				</Typography>

				{!hideSelectAllButton && entities.length ? (
					<Button
						data-testid="entity-list-select-unselect-all-button"
						variant="text"
						sx={{ width: 156, padding: 2, marginLeft: 'auto', marginRight: paddingX, textTransform: 'uppercase' }}
						onClick={() => onSelectChange?.(selected?.length ? [] : entities)}
					>
						<Tr.Portal path={selected?.length ? 'unselect-all' : 'select-all'} />
					</Button>
				) : null}
			</Stack>

			{entities.length ? (
				<List disablePadding sx={{ flexGrow: 1, maxHeight: 1.0, overflowY: 'auto', ...listSx }} {...listProps}>
					{children}
				</List>
			) : (
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexGrow: 1 }}>{empty}</Box>
			)}
		</Stack>
	);
};
